import Swiper from 'swiper/bundle';

// BARCINO — Fiche produit (hors Barba)
const barcinoProduit = {
    _el: null,
    _quantity: 1,
    _imageIndex: 0,
    _unitPrice: 0,
    _suggestionsSwiper: null,

    init() {
        this._el = document.querySelector('[data-product-detail]');
        if (!this._el) return;

        this._bindGallery();
        this._bindVariants();
        this._bindQuantity();
        this._bindLearnMore();
        this._bindDetailedProfile();
        this._bindSuggestions();
        this._refreshVariant();
    },

    destroy() {
        this._suggestionsSwiper?.destroy(true, true);
        this._suggestionsSwiper = null;
        this._el = null;
        this._quantity = 1;
        this._imageIndex = 0;
        this._unitPrice = 0;
    },

    _bindSuggestions() {
        const slider = this._el.querySelector('[data-suggestions-swiper]');
        if (!slider) return;

        this._suggestionsSwiper = new Swiper(slider, {
            slidesPerView: 'auto',
            spaceBetween: 12,
            grabCursor: true,
            a11y: { slideRole: '' },
            navigation: {
                prevEl: this._el.querySelector('[data-suggestions-previous]'),
                nextEl: this._el.querySelector('[data-suggestions-next]'),
            },
        });
    },

    _bindGallery() {
        // Scoper à la galerie : l'attribut data-product-image est aussi porté par
        // les boutons « ajouter au panier » des cartes de suggestion (URL image
        // pour l'animation panier). Sans ce scope, images.length est faussé et le
        // bouton « suivant » ne se désactive jamais à la dernière photo.
        const images = this._el.querySelectorAll('.bc-product-gallery__picture');
        if (images.length < 2) return;

        const previousButton = this._el.querySelector('[data-gallery-previous]');
        const nextButton = this._el.querySelector('[data-gallery-next]');

        const updateButton = (button, isDisabled) => {
            if (!button) return;
            button.disabled = isDisabled;
            button.setAttribute('aria-disabled', String(isDisabled));
            button.classList.toggle('_nav-disabled', isDisabled);
        };

        const show = (index) => {
            this._imageIndex = Math.max(0, Math.min(index, images.length - 1));
            images.forEach((image, imageIndex) => {
                image.classList.toggle('is-active', imageIndex === this._imageIndex);
            });
            updateButton(previousButton, this._imageIndex === 0);
            updateButton(nextButton, this._imageIndex === images.length - 1);
        };

        previousButton?.addEventListener('click', () => show(this._imageIndex - 1));
        nextButton?.addEventListener('click', () => show(this._imageIndex + 1));
        show(0);
    },

    _bindVariants() {
        this._el.querySelectorAll('input[name="product_variant"]').forEach(input => {
            input.addEventListener('change', () => this._refreshVariant());
        });
    },

    _bindQuantity() {
        this._el.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
            this._quantity = Math.max(1, this._quantity - 1);
            this._refreshQuantity();
        });
        this._el.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
            const selected = this._selectedVariant();
            const stock = Number.parseInt(selected?.dataset.variantStock || '1', 10);
            this._quantity = Math.min(stock, this._quantity + 1);
            this._refreshQuantity();
        });
    },

    _bindLearnMore() {
        this._el.querySelector('[data-open-description]')?.addEventListener('click', (event) => {
            const details = this._el.querySelector('#product-description');
            if (!details) return;

            event.preventDefault();
            details.open = true;
            details.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    },

    _bindDetailedProfile() {
        this._el.querySelector('[data-open-profile]')?.addEventListener('click', (event) => {
            const target = document.querySelector('#product-profile');
            if (!target) return;

            event.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    },

    _selectedVariant() {
        return this._el.querySelector('input[name="product_variant"]:checked');
    },

    _refreshVariant() {
        const selected = this._selectedVariant();
        if (!selected) return;

        this._el.querySelectorAll('.bc-product-variants__option').forEach(option => {
            option.classList.toggle('is-active', option.contains(selected));
        });

        const price = Number.parseFloat(selected.dataset.variantPrice || '0');
        const oldPrice = Number.parseFloat(selected.dataset.variantOldPrice || '0');
        const discountLabel = selected.dataset.variantDiscountLabel || '';
        const weight = Number.parseInt(selected.dataset.variantWeight || '0', 10);
        const stock = Number.parseInt(selected.dataset.variantStock || '0', 10);
        const pricePerKilo = weight > 0 ? price / (weight / 1000) : null;

        this._unitPrice = price;
        const priceEl = this._el.querySelector('[data-product-price]');
        priceEl.textContent = this._formatPrice(price);
        this._el.querySelector('[data-product-price-kilo]').textContent = pricePerKilo === null
            ? ''
            : `${this._formatPrice(pricePerKilo)}/kg`;

        const oldEl = this._el.querySelector('[data-product-price-old]');
        if (oldEl) {
            oldEl.textContent = oldPrice > 0 ? this._formatPrice(oldPrice) : '';
            oldEl.hidden = oldPrice <= 0;
        }
        const labelEl = this._el.querySelector('[data-product-discount-label]');
        if (labelEl) {
            labelEl.textContent = discountLabel;
            labelEl.hidden = discountLabel === '';
        }

        const stockLabel = this._el.querySelector('[data-stock-label]');
        stockLabel.classList.toggle('is-low', stock > 0 && stock <= 10);
        stockLabel.classList.toggle('is-out', stock <= 0);
        stockLabel.textContent = stock <= 0
            ? stockLabel.dataset.labelOut
            : stock <= 10
                ? stockLabel.dataset.labelLow.replace('%d', stock)
                : stockLabel.dataset.labelStock;

        this._quantity = Math.min(this._quantity, Math.max(1, stock));
        const addButton = this._el.querySelector('[data-cart-add]');
        addButton.dataset.cartAdd = selected.value;
        addButton.disabled = stock <= 0;
        this._refreshQuantity();
    },

    _refreshQuantity() {
        this._el.querySelector('[data-product-quantity]').textContent = this._quantity;
        const addButton = this._el.querySelector('[data-cart-add]');
        addButton.dataset.cartQty = String(this._quantity);
        // Le total (prix × quantité) est affiché hors du bouton, au-dessus de la
        // ligne d'achat — le libellé du bouton reste fixe (« Ajouter au panier »).
        const total = this._unitPrice * this._quantity;
        const totalEl = this._el.querySelector('[data-product-total]');
        if (totalEl) totalEl.textContent = this._formatPrice(total);
    },

    _formatPrice(price) {
        const locale = document.documentElement.lang === 'en' ? 'en-GB' : 'fr-FR';
        return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(price);
    },
};

export default barcinoProduit;
